import {Component, Inject, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {LocalStorageService} from '../../../local-storage.service';
import {Employees} from '../../models/employees-model';
import {Kostenstelle} from '../../models/kostenstelle-model';
import {DataService} from '../../services/data.service';
import {GeocodingService} from '../../services/geocoding.service';
import {ToaterMessagesService} from '../../services/toaster-messages.service';
import {MessageBoxService} from '../../../services/message-box.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-kostenstelle-dialog',
    templateUrl: './kostenstelle-dialog.component.html',
    styleUrls: ['./kostenstelle-dialog.component.scss'],
})
export class KostenstelleDialogComponent implements OnInit {
    kostenstelleForm = new UntypedFormGroup({
        kostenStelle: new UntypedFormControl(''),
        arbeitsOrt: new UntypedFormControl(''),
        strasse: new UntypedFormControl(''),
        text: new UntypedFormControl(''),
    });
    public kostenStelleModel!: Kostenstelle[];
    public userPermissions!: Employees;
    public currentLang!: string;
    private oldKostenstellenID = 0;
    // required + starts with any number (maybe 0) of any char followed by at least one non whitespace char + ends with any char so it matches " s s s " as well as "s"
    private readonly mandatoryWorkPlaceValidators = [Validators.required, Validators.pattern(new RegExp(/^.*\S+.*$/))];

    constructor(
        public dialogRef: MatDialogRef<KostenstelleDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public componentData: any,
        public localStorage: LocalStorageService,
        private toasterMessagesService: ToaterMessagesService,
        private geoCodingService: GeocodingService,
        private dataService: DataService,
        private messageBoxService: MessageBoxService,
        private translate: TranslateService
    ) {
    }

    ngOnInit(): void {
        if (!this.componentData.position?.top) {
            this.dialogRef.updatePosition({top: '3.75rem'});
        }
        this.localStorage.get('language', 'SwissTimeEntry', true).then((res: any) => {
            this.currentLang = res;
        });

        this.localStorage.get('currentTimeEntry', 'SwissTimeEntry').then((res: any) => {
            // Wenn ein Objekt vorhanden ist, die Kostenstelle dazu ermitteln
            if (res.auftrag && res.auftrag.objectNumber) {
                const costCenterByObjectID = this.dataService.GetCostCentersByObjectID(res.auftrag.objectNumber, res.auftrag.status, this.componentData.data.costs);
                costCenterByObjectID.then((res) => {
                    if (res) {
                        this.kostenStelleModel = res!;
                    }
                });
            }
        });

        this.kostenStelleModel = this.componentData.data.costs;
        this.userPermissions = this.componentData.data.userPermissions;

        this.setDialogData();

        if (this.userPermissions?.noDataEditOnHandy) {
            this.kostenstelleForm.get('kostenStelle')?.disable();
            this.kostenstelleForm.get('arbeitsOrt')?.disable();
            this.kostenstelleForm.get('text')?.disable();
        }
    }

    kostenstelleChange(value: number) {
        // Wenn die Kostenstelle ändert, wird der Wert für die Arbeitsbezeichnung neu gesetzt, aber nur wenn der Text nicht schon angepasst wurde
        if (this.kostenStelleModel) {
            const neueKostenstelle = this.kostenStelleModel.find((k) => k.costCenterID == value);
            let changeArbeitsbez = false;

            if (this.oldKostenstellenID > 0) {
                // Wurde die Kostenstelle schon mal gewechselt
                const currArbeitsbez = this.kostenstelleForm.controls.text.value;
                const origKostenstelleArbeitsbez = this.kostenStelleModel.find((k) => k.costCenterID == this.oldKostenstellenID)?.description;

                if (currArbeitsbez && origKostenstelleArbeitsbez) {
                    if (currArbeitsbez === origKostenstelleArbeitsbez) {
                        changeArbeitsbez = true;
                    }
                } else {
                    changeArbeitsbez = true;
                }
            } // Kostenstelle wurde das 1. Mal gesetzt
            else {
                changeArbeitsbez = true;
            }

            if (changeArbeitsbez) {
                const costCenter = this.kostenStelleModel.find((k) => k.costCenterID == value);
                this.kostenstelleForm.controls.text.setValue(costCenter?.description);
            }

            // Aktuelle Kostenstelle als alte Kostenstelle sichern, falls diese nochmals wechseln sollte
            this.oldKostenstellenID = value;
            let fieldValue = this.kostenstelleForm.controls.arbeitsOrt.value;

            if (neueKostenstelle?.obligatoryWorkplace) {
                this.kostenstelleForm.controls.arbeitsOrt.addValidators(this.mandatoryWorkPlaceValidators);
                if (!fieldValue || fieldValue.trim() === '') {
                    fieldValue = this.componentData.data.recommendedWorkplace;
                }
            } else {
                this.kostenstelleForm.controls.arbeitsOrt.removeValidators(this.mandatoryWorkPlaceValidators);
            }
            this.kostenstelleForm.controls.arbeitsOrt.setValue(fieldValue);
        }
    }

    protected closeDialog(): void {
        this.dialogRef.close(null);
    }

    protected next(): void {
        let kostenStelleValue: any = {};
        let close = true;
        if (this.kostenstelleForm.valid) {
            kostenStelleValue = {
                costCenterID: this.kostenstelleForm.controls.kostenStelle.value,
                description: this.kostenStelleModel.find((k) => k.costCenterID == this.kostenstelleForm.controls.kostenStelle.value)?.description,
                kostenStelle: this.kostenstelleForm.controls.kostenStelle.value,
                arbeitsOrt: this.kostenstelleForm.controls.arbeitsOrt.value?.trim() || '',
                text: this.kostenstelleForm.controls.text.value,
                obligatoryWorkplace: this.kostenstelleForm.controls.arbeitsOrt.hasValidator(Validators.required)
            };
            if (kostenStelleValue.text && kostenStelleValue.text.length > 250) {
                close = false;
                this.toasterMessagesService.toasterTranslateErrorMessages('Der Text ist zu lang.', 'Le texte est trop long.', this.currentLang);
            }
        } else {
            close = false;
            this.toasterMessagesService.toasterTranslateErrorMessages('Es fehlen zwingende Eingaben.', 'Il manque des entrées obligatoires.', this.currentLang);
        }

        if (close) {
            this.dialogRef.close({value: kostenStelleValue});
        }
    }

    protected setLocation(): void {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const longitude = position.coords.longitude;
                    const latitude = position.coords.latitude;

                    this.callApi(longitude, latitude);
                },
                () => {
                    this.translate.get('MAIN_APP_DIALOG.ERROR_DIALOG.GPS.NOT_ALLOWED.text').subscribe((t: string) => {
                        this.messageBoxService.showError(t, '');
                    });
                },
                {
                    enableHighAccuracy: true,
                }
            );
        } else {
            this.toasterMessagesService.toasterTranslateErrorMessages('Keine Unterstützung für Geolokalisierung', 'Pas de soutien pour la géolocalisation', this.currentLang);
        }
    }

    private setDialogData(): void {
        if (this.componentData.data.selectedCosts) {
            this.kostenstelleForm.controls.kostenStelle.setValue(this.componentData.data.selectedCosts.costCenterID);

            this.kostenstelleForm.controls.arbeitsOrt.setValue(this.componentData.data.selectedCosts.arbeitsOrt);

            if (this.componentData.data.selectedCosts.text) {
                this.kostenstelleForm.controls.text.setValue(this.componentData.data.selectedCosts.text);
            } else {
                this.kostenstelleForm.controls.text.setValue(this.componentData.data.selectedCosts.description);
            }

            if (this.componentData.data.selectedCosts.costCenterID) {
                const currKostenstelle = this.kostenStelleModel.find((k) => k.costCenterID == this.componentData.data.selectedCosts.costCenterID);
                if (currKostenstelle?.obligatoryWorkplace) {
                    this.kostenstelleForm.controls.arbeitsOrt.addValidators(this.mandatoryWorkPlaceValidators);
                } else {
                    this.kostenstelleForm.controls.arbeitsOrt.removeValidators(this.mandatoryWorkPlaceValidators);
                }
            }
        }
    }

    private callApi(Longitude: number, Latitude: number): void {
        this.geoCodingService.reverseGeocoding(Latitude, Longitude).subscribe({
            next: (res: any) => {
                if (res.address.city) {
                    this.kostenstelleForm.controls.arbeitsOrt.setValue(res.address.city);
                } else if (res.address.town) {
                    this.kostenstelleForm.controls.arbeitsOrt.setValue(res.address.town);
                } else if (res.address.village) {
                    this.kostenstelleForm.controls.arbeitsOrt.setValue(res.address.village);
                }
                this.kostenstelleForm.controls.strasse.setValue(res.address.road);
            },
            error: () => {
                this.translate.get('MAIN_APP_DIALOG.ERROR_DIALOG.GPS.GET_LOCATION_FAILED.text').subscribe((t: string) => {
                    this.messageBoxService.showError(t, '');
                });
            },
        });
    }
}
